import { EnumValueHumanizer, StyledDialog, useCommonStyle, useHasRole } from '@lib/common';
import { Admin } from '@lib/state';
import { DialogContent, styled, Tab, Tabs } from '@material-ui/core';
import { TabContext, TabPanel } from '@material-ui/lab';
import React, { useCallback, useEffect, useState } from 'react';
import { LocationType, PropertyDevice } from 'app/state';
import { AreaDetailsTab, DeviceDetailsTab, RoomDetailsTab } from './device-detail-tabs';
import { AuditLogs } from 'app/shared';

interface Props {
  device: PropertyDevice | null;
  open: boolean;
  onClose: () => void;
  onIdentifyDevice: (device: PropertyDevice) => void;
  onDeleteDevice: (thingName: string) => void;
}

const TabContainer = styled(TabPanel)(() => ({
  minHeight: 280,
}));

const getDefaultTab = (isAdmin: boolean) => (isAdmin ? 'details' : 'location');

export function DeviceDetailsModal({ device, open, onClose, onIdentifyDevice, onDeleteDevice }: Props) {
  const isAdmin = useHasRole(Admin);
  const { paper } = useCommonStyle();

  const [tab, setTab] = useState(getDefaultTab(isAdmin));

  const getModalTitle = useCallback(() => {
    if (!device) return 'Device';
    return `Device: ${device.thingIdentifier} - ${EnumValueHumanizer(device.thingType ?? '')}`;
  }, [device]);

  const onModalClose = useCallback(() => {
    onClose();
    setTab(getDefaultTab(isAdmin));
  }, [onClose, isAdmin]);

  useEffect(() => {
    if (open && tab === 'details' && !isAdmin) {
      setTab('location');
    }
  }, [open, isAdmin, tab]);

  return (
    <StyledDialog
      title={getModalTitle()}
      onClose={onModalClose}
      open={open}
      maxWidth="lg"
      scroll="paper"
    >
      <TabContext value={tab}>
        {device && (
          <>
            <Tabs
              value={tab}
              onChange={(_, x) => setTab(x)}
              indicatorColor="primary"
              textColor="primary"
            >
              {isAdmin && <Tab label="Device" value="details" />}
              <Tab label="Location" value="location" />
              <Tab label="Logs" value="logs" />
            </Tabs>
            <DialogContent>
              {isAdmin && (
                <TabContainer value="details">
                  <DeviceDetailsTab
                    device={device}
                    paperClassName={paper}
                    onIdentifyDevice={onIdentifyDevice}
                    onDeleteDevice={onDeleteDevice}
                    onDeleteSuccess={onModalClose}
                  />
                </TabContainer>
              )}
              <TabContainer value="location">
                {device.thingLocationType === LocationType.Room && (
                  <RoomDetailsTab device={device} paperClassName={paper} />
                )}
                {device.thingLocationType === LocationType.Area && (
                  <AreaDetailsTab device={device} paperClassName={paper} />
                )}
              </TabContainer>
              <TabContainer value="logs">
                <AuditLogs id={device.thingName ?? ''} scope="device" />
              </TabContainer>
            </DialogContent>
          </>
        )}
      </TabContext>
    </StyledDialog>
  );
}
